import { dispatchDrawerStatusEvent } from "@phn/utility/helper/events";
import registerTransitionNextLevelSingleDrawerAnimation from "./sequences/register-next-level-single-drawer-animation";
import registerTransitionPreviousLevelSingleDrawerAnimation from "./sequences/register-previous-level-single-drawer-animation";
import registerOpenSingleDrawerAnimation from "./sequences/register-open-single-drawer-animation";
import registerCloseSingleDrawerAnimation from "./sequences/register-close-single-drawer-animation";
import * as router from "../../../../../shared/services/routing-manager";
import analyticsManager from "../../../../../shared/services/analytics/analytics-manager";
import {
  DisplayElementTypes,
  ElementTypes,
  EventActions,
  FOCUSABLE_ELEMENTS_SELECTOR,
} from "../../../../../shared/utility/constants";
import { GlobalDataLayerService } from "../../../../../shared/services/analytics/global-data-layer-service";
import { routerStore } from "../../../../../shared/state/router-store";
import { navStateStore } from "../../../../../shared/state/nav-state-store";
import {
  focusOnElement,
  getHTMLElement,
  getHTMLElementFromRoot,
  isString,
} from "../../../../../shared/utility/helper";
import { DrawerStatusValues } from "../../../../../shared/utility/constants/app";

export default function registerSingleDrawerAnimationSequences(): void {
  registerTransitionNextLevelSingleDrawerAnimation({
    onAnimationEnds: () => onTransitionEnd(),
  });
  registerTransitionPreviousLevelSingleDrawerAnimation({
    onAnimationEnds: () => onTransitionEnd(),
  });
  registerOpenSingleDrawerAnimation({
    onAnimationStarts: () => {
      dispatchDrawerStatusEvent(DrawerStatusValues.startOpening);
    },
    onAnimationEnds: () => {
      onTransitionEnd();
      dispatchDrawerStatusEvent(DrawerStatusValues.finishOpening);
    },
  });
  registerCloseSingleDrawerAnimation({
    onAnimationStarts: () => {
      dispatchDrawerStatusEvent(DrawerStatusValues.startClosing);
    },
    onAnimationEnds: () => {
      dispatchDrawerStatusEvent(DrawerStatusValues.finishClosing);
      router.syncAnimation();
    },
  });
}

function onTransitionEnd() {
  const focussedElementId =
    routerStore.state.animatingPointer > routerStore.state.routingKeyPointer
      ? routerStore.state.routingKeyHistory[routerStore.state.animatingPointer]
      : "";

  router.syncAnimation();
  router.pop();

  setTimeout(() => {
    const singleDrawer = getHTMLElementFromRoot(".drawer");
    focusOn(singleDrawer, focussedElementId);
  });

  sendDataLayerEvent();
}

function sendDataLayerEvent() {
  analyticsManager.setDrawerType(DisplayElementTypes.SingleDrawer);

  GlobalDataLayerService.getInstance().pushDataLayerEvent(
    EventActions.DRAWER_LOAD,
    {
      componentDisplay: {
        displayElementType: analyticsManager.calculateDisplayElementType(
          router.getCurrentRoute(),
          ElementTypes.singleDrawer,
        ),
        displayElementId: analyticsManager.calculateDisplayElementId(),
        displayElementName: "Single Drawer",
      },
    },
  );
}

function focusOn(element: HTMLElement | null, focussedElementId: string): void {
  /* istanbul ignore if */
  if (!element) return;

  const autoFocusElement = getHTMLElement(element.querySelector(".autofocus"));
  /* istanbul ignore if */
  if (autoFocusElement !== null) {
    focusOnElement(autoFocusElement);
    return;
  }

  if (navStateStore.state.usingKeyboard) {
    let focussableElement: HTMLElement | null = null;

    /* istanbul ignore if */
    if (isString(focussedElementId)) {
      focussableElement = getHTMLElement(
        element.querySelector(`.active [data-id="${focussedElementId}"]`),
      );
    }

    if (focussableElement === null) {
      focussableElement = getHTMLElement(
        element.querySelector(`.active ${FOCUSABLE_ELEMENTS_SELECTOR}`),
      );
    }

    if (focussableElement !== null) {
      focusOnElement(focussableElement);
    }
  } else {
    const firstFocus = getHTMLElement(element.querySelector(".first-focus"));

    if (firstFocus !== null) {
      focusOnElement(firstFocus);
    }
  }
}
